import { MultiselectProps,SelectProps } from '@amzn/awsui-components-react-v3';

/**
 * Represents an empty option for select components.
 */
export const EMPTY_OPTION: SelectProps.Option = { value: '', label: '' };

/**
 * Gets the default option from an array of options.
 * @param options - An array of SelectProps.Option objects.
 * @returns The first option in the array or EMPTY_OPTION if the array is empty.
 */
export const getDefaultOption = (options: SelectProps.Option[]) => {
  if (options.length === 0) {
    return EMPTY_OPTION;
  }
  return options[0];
}

/**
 * Gets the default option from an array of region option groups.
 * @param options - An array of SelectProps.OptionGroup objects representing regions.
 * @returns The first option of the first region group or EMPTY_OPTION if no options are available.
 */
export const getRegionDefaultOption = (options: SelectProps.OptionGroup[]) => {
  if (options.length === 0) {
    return EMPTY_OPTION;
  }
  const regionOption = options[0];
  if (regionOption.options.length === 0) {
    return EMPTY_OPTION;
  }
  return regionOption.options[0];
}

/**
 * Gets the selected option based on the provided value.
 * @param options - An array of SelectProps.Option objects.
 * @param selectedValue - The value of the selected option.
 * @param defaultOption - The default option to return if no match is found.
 * @returns The matching option or the default option if no match is found.
 */
export const getSelectedOption = (
    options: SelectProps.Option[],
    selectedValue: string,
    defaultOption: SelectProps.Option
): SelectProps.Option => {
  return options.find(option => option.label === selectedValue) ?? defaultOption;
}

/**
 * Gets an array of selected options based on the provided values.
 * @param options - An array of SelectProps.Option objects.
 * @param selectedValues - An array of selected option values.
 * @returns An array of MultiselectProps.Option objects matching the selected values.
 */
export const getSelectedOptions = (
    options: SelectProps.Option[],
    selectedValues: string[]
): MultiselectProps.Option[] => {
  return options.filter(option => selectedValues.includes(option.label!));
}

/**
 * Gets the selected option from region option groups based on the provided value.
 * @param options - An array of SelectProps.OptionGroup objects representing regions.
 * @param selectedValue - The value of the selected option.
 * @param defaultOption - The default option to return if no match is found.
 * @returns The matching option or the default option if no match is found.
 */
export const getRegionSelectedOption = (
  options: SelectProps.OptionGroup[],
  selectedValue: string,
  defaultOption: SelectProps.Option
): SelectProps.Option => {
  for (const regionOption of options) {
    for (const clusterOption of regionOption.options) {
      if (clusterOption.label === selectedValue) {
        return clusterOption;
      }
    }
  }
  return defaultOption;
}

/**
 * Gets an array of selected options from region option groups based on the provided values.
 * @param options - An array of SelectProps.OptionGroup objects representing regions.
 * @param selectedValues - An array of selected option values.
 * @returns An array of MultiselectProps.Option objects matching the selected values.
 */
export const getRegionSelectedOptions = (
  options: SelectProps.OptionGroup[],
  selectedValues: string[]
): MultiselectProps.Option[] => {
  const selectedOptions: MultiselectProps.Option[] = [];
  for (const regionOption of options) {
    for (const clusterOption of regionOption.options) {
      if (selectedValues.includes(clusterOption.label!)) {
        selectedOptions.push(clusterOption);
      }
    }
  }
  return selectedOptions;
}